import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function ContactPage() {
  return (
    <div className="min-h-screen bg-white text-gray-900 font-['Inter',sans-serif]">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-50 via-white to-red-50">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-bl from-orange-400/20 to-transparent rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-tr from-red-400/20 to-transparent rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <div className="text-orange-700 text-xs uppercase tracking-wider font-semibold mb-4">
              Get In Touch
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Let's Build Something<br />Intelligent Together
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed">
              Ready to transform your business with AI? Our team is here to discuss your project 
              and explore how FluxByte can help you achieve your goals.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Send Us a Message
              </h2>
              <form 
                id="contact_form"
                data-readdy-form
                action="https://readdy.ai/api/form/d51e2utmdo3ncn62he60"
                method="POST"
                className="space-y-6"
                onSubmit={(e) => {
                  e.preventDefault();
                  const form = e.currentTarget;
                  const formData = new FormData(form);
                  
                  const name = formData.get('name') as string;
                  const email = formData.get('email') as string;
                  const company = formData.get('company') as string;
                  const message = formData.get('message') as string;

                  if (!name || !email || !company || !message) {
                    alert('Please fill in all required fields');
                    return;
                  }

                  if (message.length > 500) {
                    alert('Message must be 500 characters or less');
                    return;
                  }

                  fetch('https://readdy.ai/api/form/d51e2utmdo3ncn62he60', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    body: new URLSearchParams(formData as any).toString()
                  })
                  .then(response => {
                    if (response.ok) {
                      alert('Thank you! We will be in touch soon.');
                      form.reset();
                    } else {
                      alert('Something went wrong. Please try again.');
                    }
                  })
                  .catch(() => {
                    alert('Something went wrong. Please try again.');
                  });
                }}
              >
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-gray-900 text-sm font-semibold mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors text-sm"
                      placeholder="John Smith"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-gray-900 text-sm font-semibold mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors text-sm"
                      placeholder="john@company.com"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="company" className="block text-gray-900 text-sm font-semibold mb-2">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    required
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors text-sm"
                    placeholder="Your Company Ltd"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-gray-900 text-sm font-semibold mb-2">
                    Tell us about your project *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={6}
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors resize-none text-sm"
                    placeholder="Describe your AI needs and business challenges..."
                  ></textarea>
                  <div className="text-gray-600 text-xs mt-1">Maximum 500 characters</div>
                </div>

                <button
                  type="submit"
                  className="w-full px-8 py-4 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-lg font-semibold text-base hover:shadow-xl hover:shadow-orange-500/50 transition-all cursor-pointer whitespace-nowrap"
                >
                  Send Message
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Other Ways to Reach Us
              </h2>

              <div className="space-y-8">
                {/* Email */}
                <div className="flex gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-lg flex-shrink-0">
                    <i className="ri-mail-line text-2xl text-orange-600"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">Email Us</h3>
                    <p className="text-gray-600 text-sm mb-2">
                      For general inquiries and project discussions
                    </p>
                    <a href="mailto:hello@fluxbyte.ai" className="text-orange-600 font-semibold hover:underline cursor-pointer">
                      hello@fluxbyte.ai
                    </a>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-lg flex-shrink-0">
                    <i className="ri-phone-line text-2xl text-orange-600"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">Call Us</h3>
                    <p className="text-gray-600 text-sm mb-2">
                      Monday to Friday, 9am - 6pm GMT
                    </p>
                    <a href="tel:+441618837750" className="text-orange-600 font-semibold hover:underline cursor-pointer">
                      0161 883 7750
                    </a>
                  </div>
                </div>

                {/* Location */}
                <div className="flex gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-lg flex-shrink-0">
                    <i className="ri-map-pin-line text-2xl text-orange-600"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">Visit Us</h3>
                    <p className="text-gray-600 text-sm">
                      FluxByte Ltd<br />
                      Canal Road<br />
                      Altrincham, WA14 1AE<br />
                      United Kingdom
                    </p>
                  </div>
                </div>

                {/* Social */}
                <div className="flex gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-lg flex-shrink-0">
                    <i className="ri-share-line text-2xl text-orange-600"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-3">Follow Us</h3>
                    <div className="flex gap-3">
                      <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-full hover:bg-orange-500 hover:text-white transition-colors cursor-pointer">
                        <i className="ri-linkedin-fill text-lg"></i>
                      </a>
                      <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-full hover:bg-orange-500 hover:text-white transition-colors cursor-pointer">
                        <i className="ri-twitter-x-fill text-lg"></i>
                      </a>
                      <a href="https://github.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-full hover:bg-orange-500 hover:text-white transition-colors cursor-pointer">
                        <i className="ri-github-fill text-lg"></i>
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Map Placeholder */}
              <div className="mt-12 w-full h-64 bg-gray-100 rounded-xl overflow-hidden">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2378.5!2d-2.3522!3d53.3838!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x487bad8e0e5e5e5f%3A0x0!2sCanal%20Road%2C%20Altrincham%20WA14%201AE!5e0!3m2!1sen!2suk!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="FluxByte Office Location - Canal Road, Altrincham"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-gray-600 text-base">
              Quick answers to common questions about working with FluxByte
            </p>
          </div>

          <div className="space-y-4">
            {[
              {
                question: 'What types of AI projects do you work on?',
                answer: 'We work on a wide range of AI projects including machine learning models, intelligent automation systems, predictive analytics, natural language processing, computer vision, and custom AI solutions tailored to specific business needs.'
              },
              {
                question: 'How long does a typical AI project take?',
                answer: 'Project timelines vary based on complexity and scope. Simple automation projects can be completed in 4-8 weeks, while complex machine learning systems may take 3-6 months. We provide detailed timelines during the initial consultation.'
              },
              {
                question: 'Do you provide ongoing support after deployment?',
                answer: 'Yes, we offer comprehensive post-deployment support including monitoring, optimization, maintenance, and continuous improvement to ensure your AI systems deliver long-term value.'
              },
              {
                question: 'What industries do you serve?',
                answer: 'We serve clients across multiple industries including technology, finance, healthcare, manufacturing, retail, logistics, and more. Our AI solutions are adaptable to various business contexts and challenges.'
              },
              {
                question: 'How do you ensure data security and privacy?',
                answer: 'We implement industry-leading security practices including encryption, secure data handling, compliance with GDPR and other regulations, and transparent data governance policies to protect your sensitive information.'
              }
            ].map((faq, index) => (
              <details 
                key={index}
                className="bg-white border border-gray-200 rounded-lg p-6 cursor-pointer group"
              >
                <summary className="flex items-center justify-between font-semibold text-gray-900 text-base cursor-pointer">
                  {faq.question}
                  <i className="ri-arrow-down-s-line text-xl text-gray-400 group-open:rotate-180 transition-transform"></i>
                </summary>
                <p className="mt-4 text-gray-600 text-sm leading-relaxed">
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
